import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components/macro';
import { useField, Input as InputFromUnform } from '@rocketseat/unform';
import ReactDatepicker from 'react-datepicker';
import { isValidDate } from 'src/utils/date.utils';
import { isValid } from 'date-fns';
import { getBrandingColor, colorTypes } from 'src/branding';
import {
  Label, Column, Row, Cell,
} from './Layout';
import { StyledButton } from './Button';
import 'react-datepicker/dist/react-datepicker.css';
import { propsUtils } from '../utils';

export const InputStyleMixin = css`
  border: 1px solid ${({ error }) => (error ? getBrandingColor(colorTypes.error) : '#dddada')};
  background-color: ${getBrandingColor(colorTypes.mainBg)};
  color: ${getBrandingColor(colorTypes.fontNormal)};
  font-size: 1rem;
  padding: ${({ small }) => (small ? '10px 14px' : '12px 18px')};
  width: 100%;
  box-sizing: border-box;
  margin: 8px 0;
  border-radius: 3px;

  :focus {
    /* padding: ${({ small }) => (small ? '9px 13px' : '11px 17px')}; */
    border: 1px solid #b4b4b4;
    outline: none;
    border-color: ${({ error }) => (error ? getBrandingColor(colorTypes.error) : getBrandingColor(colorTypes.fontNormal))};
  }
`;

const StyledUnformInput = styled(InputFromUnform)`
  ${InputStyleMixin}
  box-sizing: border-box;
`;

const InputFieldV2Wrapper = styled.div`
  /*Validation error messages*/
  display: inline-block;
  input + span {
    font-weight: 600;
    margin-left: 12px;
    margin-right: 12px;
  }
`;

export const InputField = ({
  name, label, options, multiple, ...rest
}) => {
  const ref = useRef(null);
  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);
  const [value, setValue] = useState(null);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: ref.current,
      path: 'props.value',
      clearValue: (selectRef) => {
        setValue('');
      },
    });
  }, [ref.current, fieldName]); // eslint-disable-line

  return (
    <InputFieldV2Wrapper>
      <StyledUnformInput
        error={error}
        name={fieldName}
        value={value === null ? defaultValue : value}
        onChange={(evt) => setValue(evt.target.value)}
        ref={ref}
        {...rest}
      />
    </InputFieldV2Wrapper>
  );
};

export const HtmlLabel = styled(Column.withComponent('label'))`
  position: relative;
  flex: 1 1 auto;
`;
export const DatepickerLabel = styled.span`
  position: relative;

  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    width: fit-content;

    ${({ wide }) => (wide
  ? css`
            button {
              :not(.react-datepicker__close-icon) {
                width: 183px;
              }
            }
          `
  : '')}
  }

  .react-datepicker {
    border: 1px solid ${getBrandingColor(colorTypes.blue)};
    box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.1);

    &-popper[data-placement^='bottom'] .react-datepicker__triangle {
      border-bottom-color: ${getBrandingColor(colorTypes.blue)};

      ::before {
        border-bottom-color: ${getBrandingColor(colorTypes.cellBorder)};
      }
    }

    &__time-list-item--selected,
    &__time-list-item--selected {
      background-color: ${getBrandingColor(colorTypes.blue)};
    }

    .react-datepicker__time-container
      .react-datepicker__time
      .react-datepicker__time-box
      ul.react-datepicker__time-list
      li.react-datepicker__time-list-item--selected {
      background-color: ${getBrandingColor(colorTypes.blue)};
    }

    .react-datepicker__time-box {
      border-radius: 0;
    }

    &-popper {
      z-index: 3;
    }

    &__header {
      padding-left: 8px;
      padding-right: 8px;
      background-color: #0a62cd;
      border-bottom: 1px solid ${getBrandingColor(colorTypes.blue)};
      border-top-left-radius: 2px;
    }

    &-time__header,
    &__current-month,
    &__day-name {
      color: #fff;
    }

    &__navigation--previous {
      border-right-color: #fff;
    }

    &__navigation--next {
      border-left-color: #fff;
      :hover {
        border-left-color: #fff;
      }
    }

    &__day--selected,
    &__month-text--selected,
    &__day--keyboard-selected,
    &__month-text--keyboard-selected {
      &,
      :hover {
        background-color: ${getBrandingColor(colorTypes.link)};
      }
    }
    &__day--keyboard-selected,
    &__month-text--keyboard-selected {
      &,
      :hover {
        background-color: #ccc;
      }
    }

    /*Container for year-dropdown, enabled using showYearDropdown */
    &__year-read-view {
      position: relative;
      top: -2px;
      left: -7px;
    }

    /*Label for dropdown, enabled using showYearDropdown */
    &__year-read-view--selected-year {
      color: white;
    }

    &__year-read-view--down-arrow {
      top: 7px;
    }

    /*Clear button*/
    &__close-icon {
    }
  }
`;

export const Input = styled.input`
  ${InputStyleMixin}
`;

export const NumericInput = ({ children, ...props }) => (
  <Input
    type="number"
    inputmode="numeric"
    pattern="[0-9]*"
    {...props}
    className="noscroll"
  >
    {children}
  </Input>
);

const StyledUnformMultilineInput = styled(
  StyledUnformInput.withComponent('textarea'),
)`
  border: 1px solid ${({ error }) => (error ? getBrandingColor(colorTypes.error) : getBrandingColor(colorTypes.fontNormal))};
  background-color: ${getBrandingColor(colorTypes.mainBg)};
  color: ${getBrandingColor(colorTypes.fontNormal)};
  font-size: 1rem;
  padding: ${({ small }) => (small ? '10px 14px' : '12px 18px')};
  width: 100%;
  box-sizing: border-box;
  margin: 8px 0;

  :focus {
    padding: ${({ small }) => (small ? '9px 13px' : '11px 17px')};
    border: 2px solid
      ${({ error }) => (error ? getBrandingColor(colorTypes.error) : getBrandingColor(colorTypes.fontNormal))};
  }

  resize: vertical;
`;

const StyledDatePicker = styled(ReactDatepicker)`
  border: 1px solid ${({ error }) => (error ? getBrandingColor(colorTypes.error) : getBrandingColor(colorTypes.fontNormal))};
  background-color: ${getBrandingColor(colorTypes.mainBg)};
  color: ${getBrandingColor(colorTypes.fontNormal)};
  font-size: 1rem;
  padding: ${({ small }) => (small ? '10px 14px' : '12px 18px')};
  width: 100%;
  box-sizing: border-box;
  margin: 8px 0;

  :focus {
    padding: ${({ small }) => (small ? '9px 13px' : '11px 17px')};
    border: 2px solid
      ${({ error }) => (error ? getBrandingColor(colorTypes.error) : getBrandingColor(colorTypes.fontNormal))};
  }
`;

const InputWrapper = styled.span`
  position: relative;
  display: ${({ inline }) => (inline ? 'inline-block' : 'block')};
`;

// const ClearButton = styled.div`
//   display: flex;
//   align-items: center;
//   position: absolute;
//   padding: 0 0.5em;
//   top: 0;
//   bottom: 0;
//   vertical-align: middle;
//   right: 0.2em;
//   cursor: pointer;
//   font-size: 0.8em;
// `;

const CheckboxStyleWrapper = styled.div`
  /*
  /* Styling of checkbox mark https://stackoverflow.com/a/48718254 */
  input[type='checkbox']:checked + label::after {
    content: '';
    position: absolute;
    width: 1.2ex;
    height: 0.4ex;
    background: rgba(0, 0, 0, 0);
    top: 0.8ex;
    left: 0.6ex;
    border: 3px solid ${getBrandingColor(colorTypes.primary)};
    border-top: none;
    border-right: none;
    -webkit-transform: rotate(-45deg);
    -moz-transform: rotate(-45deg);
    -o-transform: rotate(-45deg);
    -ms-transform: rotate(-45deg);
    transform: rotate(-45deg);
  }

  input[type='checkbox'] {
    line-height: 2.1ex;
  }

  input[type='checkbox']:focus + label::before {
    border: 1px solid gray;
  }

  input[type='radio'],
  input[type='checkbox'] {
    position: absolute;
    left: -999em;
  }

  input[type='checkbox'] + label {
    position: relative;
    overflow: hidden;
    cursor: pointer;
  }

  input[type='checkbox'] + label::before {
    content: '';
    display: inline-block;
    vertical-align: -25%;
    height: 2.4ex;
    width: 2.4ex;
    background-color: ${getBrandingColor(colorTypes.whiteAltBg)};
    border: 1.115px solid rgba(78, 122, 185, 0.22);
    border-radius: 4px;
    margin-right: 0.5em;
    margin-top: 0.5em;
    box-sizing: border-box;
  }
`;

export const Checkbox = ({
  label, onChange, value, ...props
}) => (
  <CheckboxStyleWrapper>
    <input
      type="checkbox"
      {...props}
      value={value}
      onChange={(evt) => onChange(evt.target.value)}
    />
    <label htmlFor="">{label}</label>
  </CheckboxStyleWrapper>
);

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  margin-top: -8px;
  width: fit-content;
  z-index: 2;
`;

export const ChoiceButton = styled(StyledButton)`
  padding: ${({ small }) => (small ? '0px' : '12px 18px')};
  background-color: #f0f0f0;
  border: 1px solid #eff0f1;
  border-radius: 5px;
  :not(:disabled){
    color: black;
  }
  :hover {
    background-color: #e6e6e6;
  }
  &:active{
    color:  inherit;
  }

  ${({ selected, small }) => selected
    && css`
      border: 1px solid ${getBrandingColor(colorTypes.primary)};
      color: ${getBrandingColor(colorTypes.primary)};
    `}
`;

const Error = ({ children }) => (
  <Label
    marginTop="-8px"
    marginBottom="2px"
    marginLeft="2px"
    size="0.9em"
    lineHeight="2em"
    color="black"
  >
    {children}
  </Label>
);

Error.propTypes = {
  children: PropTypes.node.isRequired,
};

export const UnformInput = ({
  name,
  label,
  placeholder,
  small,
  multiline,
  dropdown,
  onChange,
  onFocus,
  children,
  error: errorProp,
  ...props
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: ref.current,
      path: 'state.value',
      clearValue: (ref) => {
        ref.clear();
      },
    });
  }, [ref.current, fieldName]); // eslint-disable-line

  let Input;

  if (multiline) {
    Input = StyledUnformMultilineInput;
  } else {
    Input = StyledUnformInput;
  }

  return (
    <HtmlLabel grow>
      {label && (
        <Label
          color={getBrandingColor(colorTypes.fontNormal)}
          marginLeft="4px"
          marginTop="6px"
          marginBottom="-2px"
        >
          {label}
        </Label>
      )}
      <InputWrapper>
        <Input
          ref={ref}
          placeholder={placeholder}
          multiline={multiline}
          onFocus={() => {
            if (onFocus) {
              onFocus();
            }
            setDropdownOpen(true);
            return true;
          }}
          name={fieldName}
          value={value}
          small={small}
          onChange={(e) => {
            if (onChange) {
              onChange(e.target.value);
            }
            setValue(e.target.value);
            e.stopPropagation();
          }}
          {...(dropdown ? { autoComplete: 'off' } : {})}
          {...props}
        />
        {dropdownOpen && dropdown && (
          <DropdownMenu ref={dropdownRef}>{dropdown}</DropdownMenu>
        )}
        {children}
      </InputWrapper>
      {(error || errorProp) && <Error>{error || errorProp}</Error>}
    </HtmlLabel>
  );
};

Input.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  small: PropTypes.bool,
  multiline: PropTypes.bool,
  dropdown: PropTypes.node,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  children: PropTypes.node,
  error: PropTypes.string,
};

Input.defaultProps = {
  label: null,
  small: false,
  multiline: false,
  dropdown: null,
  onChange: null,
  onFocus: null,
  children: null,
  error: null,
};
export const UncontrolledInputChoices = ({
  label,
  small,
  name,
  choices,
  ignoreInForm,
  values,
  disabledValues,
  onChange,
  justify,
  flexBasis,
  value: valueProp,
  error: errorProp,
  children,
}) => {
  const ref = useRef(null);
  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);
  const [selected, setSelected] = useState(defaultValue || valueProp);
  const [value, setValue] = useState(defaultValue || valueProp);

  useEffect(() => {
    if (!ignoreInForm) {
      registerField({
        name: fieldName,
        ref: ref.current,
        path: 'value',
        clearValue: (pickerRef) => {
          pickerRef.clear();
        },
      });
    }
  }, [ref.current, fieldName]); // eslint-disable-line

  useEffect(() => {
    if (selected !== (defaultValue || valueProp) && onChange) {
      onChange(selected);
    }

    if (ref.current) {
      setValue(selected);
    }
  }, [selected]); // eslint-disable-line

  useEffect(() => {
    setSelected(valueProp);
  }, [valueProp]);

  return (
    <Column grow style={{ maxWidth: '100%' }}>
      {label && (
        <Label
          color={getBrandingColor(colorTypes.fontNormal)}
          marginLeft="4px"
          marginTop="6px"
          marginBottom="-2px"
        >
          {label}
        </Label>
      )}
      <Row marginTop="8px" marginBottom="8px" wrap justify={justify}>
        {choices.map((choice, key) => (
          <Cell flexBasis={flexBasis} key={key}>
            <Cell grow padding="0.4em">
              <ChoiceButton
                key={key}
                grow
                small={small}
                selected={values ? selected === values[key] : selected === key}
                disabled={
                  values && disabledValues
                    ? disabledValues.indexOf(values[key]) >= 0
                    : false
                }
                onClick={(e) => {
                  e.preventDefault();
                  setSelected(values ? values[key] : key);
                }}
              >
                {choice}
              </ChoiceButton>
            </Cell>
          </Cell>
        ))}
        {children}
      </Row>
      <input ref={ref} type="hidden" name={fieldName} value={value || ''} />
      {(error || errorProp) && <Error>{error || errorProp}</Error>}
    </Column>
  );
};

UncontrolledInputChoices.propTypes = {
  label: PropTypes.string,
  small: PropTypes.bool,
  name: PropTypes.string.isRequired,
  choices: PropTypes.any.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  ),
  onChange: PropTypes.func,
  value: PropTypes.any,
  children: PropTypes.node,
  error: PropTypes.string,
  justify: PropTypes.string,
  flexBasis: PropTypes.string,
};

UncontrolledInputChoices.defaultProps = {
  label: null,
  small: false,
  values: null,
  onChange: null,
  value: null,
  children: null,
  error: null,
  justify: null,
  flexBasis: null,
};

// a controlled input for choosing between one of several choices
// state is kept on the component using this component through value(selected) and values(choices)
export const InputChoices = React.memo(({
  label,
  small,
  name,
  choices,
  ignoreInForm,
  values,
  disabledValues,
  onChange,
  justify,
  flexBasis,
  value: valueProp,
  error: errorProp,
  children,
  smallbuttons,
}) => {
  const ref = useRef(null);

  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);

  useEffect(() => {
    if (!ignoreInForm) {
      registerField({
        name: fieldName,
        ref: ref.current,
        path: 'value',
        clearValue: (pickerRef) => {
          pickerRef.clear();
        },
      });
    }
  }, [ref.current, fieldName]); // eslint-disable-line

  return (
    <Column grow style={{ maxWidth: '100%' }}>
      {label && (
        <Label
          color={getBrandingColor(colorTypes.fontNormal)}
          marginLeft="4px"
          marginTop="6px"
          marginBottom="-2px"
        >
          {label}
        </Label>
      )}
      <Row
        marginTop="8px"
        marginBottom="8px"
        wrap
        justify={justify}
        style={{ minWidth: '240px', display: smallbuttons ? 'block' : 'flex' }}
      >
        {choices.map((choice, key) => (
          <Cell
            flexBasis={flexBasis}
            key={key}
            style={{ display: smallbuttons ? 'inline-block' : 'flex' }}
          >
            <Cell grow padding="0.4em">
              <ChoiceButton
                key={key}
                grow
                small={small}
                selected={valueProp === values[key]}
                disabled={values && disabledValues
                  ? disabledValues.indexOf(values[key]) >= 0
                  : false}
                onClick={(e) => {
                  e.preventDefault();
                  onChange(values ? values[key] : key);
                }}
              >
                {choice}
              </ChoiceButton>
            </Cell>
          </Cell>
        ))}
        {children}
      </Row>
      <input ref={ref} type="hidden" name={fieldName} value={valueProp || ''} />
      {(error || errorProp) && <Error>{error || errorProp}</Error>}
    </Column>
  );
});

InputChoices.propTypes = {
  label: PropTypes.string,
  small: PropTypes.bool,
  name: PropTypes.string.isRequired,
  choices: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  ),
  onChange: PropTypes.func,
  value: PropTypes.any,
  children: PropTypes.node,
  error: PropTypes.string,
  justify: PropTypes.string,
  flexBasis: PropTypes.string,
};

InputChoices.defaultProps = {
  label: null,
  small: false,
  values: null,
  onChange: null,
  value: null,
  children: null,
  error: null,
  justify: null,
  flexBasis: null,
};

const SelectMultipleItemLabel = styled.label`
  display: block;
  min-width: 200px;
  ::after {
    content: '';
    display: block;
  }
`;

const SelectMultipleWrapper = styled.div`
  display: block;
  display: flex;
  flex-direction: column;
  overflow: auto;

  ${({ height }) => (height
  ? css`
          height: ${height};
        `
  : css`
          height: 200px;
        `)}
`;

/**
 * A list of checkboxes to be used in a unform-form
 * The list is populated by options: eg [{id: 1, title:"title2"}, {id: 2, title:"title3"}]
 * Data is returned as a list of the selected ids, the same format is returned when the selction changes in onChange
 */
export const SelectMultiple = ({
  label,
  name,
  options,
  onChange,
  errorProp,
  displayCount,
  isRequired,
  height,
}) => {
  const ref = useRef(null);
  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);
  const [values, setValues] = useState(defaultValue);

  const getSelectedIds = (data) => data.map((e) => e.id);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: ref.current,
      path: 'value',
      parseValue: (ref) => {
        const selectedObj = JSON.parse(ref.value);
        return getSelectedIds(selectedObj);
      },
      clearValue: () => {
        setValues({});
      },
    });
  }, [ref.current, fieldName]); // eslint-disable-line

  return (
    <Column grow>
      {label && (
        <Label
          color={getBrandingColor(colorTypes.fontNormal)}
          marginLeft="4px"
          marginTop="6px"
          marginBottom="2px"
        >
          {label}
          {displayCount && ` (${values.length})`}
        </Label>
      )}
      <SelectMultipleWrapper height={height}>
        {options.map((opt) => (
          <SelectMultipleItemLabel isRequired={isRequired}>
            <input
              key={opt.id}
              type="checkbox"
              name={name}
              checked={values.map((v) => v.id).indexOf(opt.id) >= 0}
              value={opt.id}
              onChange={(e) => {
                const isChecked = e.target.checked;
                const valuesToChange = values.slice();

                // a new item has been checked and is to bee added
                if (isChecked) {
                  valuesToChange.push({
                    id: parseInt(e.target.value, 10),
                    title: options.find(
                      (opt) => opt.id === parseInt(e.target.value, 10),
                    ).title,
                  });
                }

                // the item is unckeched and is removed
                else {
                  const existingElemIdx = values
                    .map((v) => v.id)
                    .indexOf(parseInt(e.target.value, 10));
                  valuesToChange.splice(existingElemIdx, 1);
                }
                setValues(valuesToChange);
                if (onChange) {
                  onChange(getSelectedIds(valuesToChange));
                }
              }}
            />
            <span aria-label={isRequired ? 'Påkrevd felt' : ''}>
              {opt.title}
              {isRequired ? '*' : ''}
            </span>
          </SelectMultipleItemLabel>
        ))}
      </SelectMultipleWrapper>
      <input
        ref={ref}
        type="hidden"
        name={fieldName}
        value={JSON.stringify(values) || {}}
      />
      {(error || errorProp) && <Error>{error || errorProp}</Error>}
    </Column>
  );
};

const selectMultipleShape = PropTypes.arrayOf(
  PropTypes.shape({ id: PropTypes.number, title: PropTypes.any }),
);
SelectMultiple.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: selectMultipleShape.isRequired,
  displayCount: PropTypes.bool,
  onChange: PropTypes.func,
  errorProp: PropTypes.any,
  isRequired: PropTypes.bool,
  height: PropTypes.string,
};

SelectMultiple.defaultProps = {
  label: null,
  onChange: null,
  errorProp: null,
  isRequired: false,
  displayCount: false,
  height: '150px',
};

/**
 * To be used in a unform-form.
 *
 * Has two modes of operation:
 *    *  (default) the date value(a valid date string or a date object) is set by the client each render. Value changes are spotted using onChange
 *     * (activated by setting valueControlledByForm to true) a default value may be specified, but value and value changes ae not handled by the client. The final value is retired on form submission.
 */
export const DatePicker = ({
  name,
  label,
  small,
  simple,
  placeholder,
  onChange,
  children,
  error: errorProp,
  showTimeInput,
  timeOnly,
  // showClearButton,
  timeIntervals,
  valueControlledByForm,
  value,
  ...props
}) => {
  const ref = useRef(null);
  const {
    fieldName, registerField, defaultValue, error,
  } = useField(name);
  const [date, setDate] = useState(value || defaultValue);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: ref.current,
      path: 'props.selected',
      clearValue: (pickerRef) => {
        pickerRef.clear();
      },
    });
  }, [ref.current, fieldName]); // eslint-disable-line

  useEffect(() => {
    if (!valueControlledByForm) {
      if (isValidDate(value)) {
        setDate(value);
      } else if (value) {
        setDate(new Date(value));
      } else {
        setDate(null);
      }
    }
  }, [value, valueControlledByForm]);

  const SimpleDatePicker = ({ value, onClick }) => (
    <div
      {...propsUtils.onClick(onClick)}
      css={`
        margin-top: 0.2em;
        width: 100%;
        ${!value
          && css`
            color: ${getBrandingColor(colorTypes.fontNormal)};
          `}
      `}
    >
      {value || placeholder}
    </div>
  );

  const DatePickerButton = ({ value, onClick }) => (
    <ChoiceButton
      secondary
      small={small}
      onClick={onClick}
      css={
        !value
        && css`
          color: ${getBrandingColor(colorTypes.fontNormal)};
        `
      }
    >
      {value || 'Velg dato'}
    </ChoiceButton>
  );

  const TimePickerButton = ({ value, onClick }) => (
    <ChoiceButton
      secondary
      small={small}
      onClick={onClick}
      css={`
        margin-left: 6px;
        ${!value
          && css`
            color: ${getBrandingColor(colorTypes.fontNormal)};
          `}
      `}
    >
      {value || '00:00'}
    </ChoiceButton>
  );

  SimpleDatePicker.propTypes = {
    value: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
  };

  DatePickerButton.propTypes = {
    value: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
  };

  TimePickerButton.propTypes = {
    value: PropTypes.node.isRequired,
    onClick: PropTypes.func.isRequired,
  };

  return (
    // eslint-disable-next-line react/destructuring-assignment
    <DatepickerLabel grow wide={props.isClearable}>
      {label && (
        <Label
          color={getBrandingColor(colorTypes.fontNormal)}
          marginLeft="4px"
          marginTop="6px"
          marginBottom="-2px"
        >
          {label}
        </Label>
      )}
      <Row marginTop={!simple && '8px'} marginBottom={!simple && '8px'}>
        {simple ? (
          <StyledDatePicker
            ref={ref}
            small={small}
            dateFormat="d. MMMM yyyy HH:mm"
            name={fieldName}
            selected={isValid(date) ? date : null}
            onChange={(date) => {
              setDate(date);
              onChange(date);
            }}
            customInput={<SimpleDatePicker />}
          />
        ) : (
          <>
            {!timeOnly && (
              <StyledDatePicker
                ref={ref}
                small={small}
                dateFormat="d. MMMM yyyy"
                name={fieldName}
                selected={isValid(date) ? date : null}
                onChange={(date) => {
                  setDate(date);
                  if (onChange) {
                    onChange(date);
                  }
                }}
                customInput={<DatePickerButton />}
                {...props}
              />
            )}
            {(showTimeInput || timeOnly) && (
              <StyledDatePicker
                small={small}
                placeholderText="00:00"
                timeInput
                showTimeSelect
                showTimeSelectOnly
                timeInputLabel="Klokkeslett"
                dateFormat="HH:mm"
                timeIntervals={timeIntervals || 30}
                name={fieldName}
                selected={isValid(date) ? date : null}
                onChange={(date) => {
                  setDate(date);
                  onChange(date);
                }}
                customInput={<TimePickerButton />}
                {...props}
              />
            )}
          </>
        )}
      </Row>
      {children}
      {(error || errorProp) && <Error>{error || errorProp}</Error>}
    </DatepickerLabel>
  );
};

DatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  small: PropTypes.bool,
  simple: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  children: PropTypes.node,
  error: PropTypes.string,
  showTimeInput: PropTypes.bool,
  timeOnly: PropTypes.bool,
  // showClearButton: PropTypes.bool,
  timeIntervals: PropTypes.number,
  value: PropTypes.any,

  // If the value should be controlled by an unform-form, else the valeu have to be set expicitly each render
  valueControlledByForm: PropTypes.bool,
};

DatePicker.defaultProps = {
  label: null,
  placeholder: null,
  small: false,
  simple: false,
  onChange: null,
  children: null,
  error: null,
  showTimeInput: false,
  timeOnly: false,
  // showClearButton: false,
  timeIntervals: null,
  value: null,
  valueControlledByForm: false,
};
